import React from 'react';
import type { MoFamily } from '../pages/mofamily/types';
import { FamilyAvatar } from './FamilyAvatar';
import { OwnerOperatorLine } from './FamilyCard';

/**
 * 체결된 바터의 양쪽 자기-owner 모패밀리 (a + with-b) 를 한 카드로 표현.
 * 일반 카드와 비슷한 height 유지 — 하위 모패밀리/슬롯 라인은 생략.
 *
 * Layout:
 *   [primary 패밀리 영역 — clickable]
 *      avatar + name + 🔄 + 작은 ↔ pairFamilyName
 *   ─────
 *   [secondary 패밀리 영역 — clickable]
 *      avatar + name + 작은 ↔ pairFamilyName
 *   ─────
 *   owner avatar + name (둘 다 동일 owner)
 */
export const BarterPairCard: React.FC<{
 primary: MoFamily;
 secondary: MoFamily;
 onDetail: (familyHash: string) => void;
}> = ({ primary, secondary, onDetail }) => {
 // owner 는 내 소유(목록은 항상 내 소유)면 정보량이 없어 숨긴다. 비-소유 예외에서만 표시.
 const isOwner = primary.isOwner || primary.role === 'owner';
 const ownerName = isOwner ? null : primary.ownerName;
 // 같은 매칭이라 양쪽 짝꿍 정보가 동일 — 한 번만 표시.
 const pairName = primary.pairFamilyName || secondary.pairFamilyName;

 // 상세/목록 카드와 동일 원칙: 내가 제어할 수 있는 슬롯이 없으면 사선 배경 (사선 = 제어 불가).
 // 바터 쌍은 서로 다른 두 패밀리(내 바터 a + 짝 with-b)라, 행마다 각자의 제어 가능 여부로 개별 판정한다.
 const noControlDiagonal = 'repeating-linear-gradient(45deg, rgba(96,125,139,0.14) 0, rgba(96,125,139,0.14) 5px, transparent 5px, transparent 12px)';

 const renderRow = (f: MoFamily, withRotateIcon: boolean) => {
  const rowNoControl = (f.totalSlots ?? 0) > 0 && f.hasControllableSlot === false;
  return (
  <div
   className="flex items-start gap-3 p-3 cursor-pointer hover:bg-gray-50"
   style={rowNoControl ? { backgroundImage: noControlDiagonal } : undefined}
   title={rowNoControl ? '내가 제어할 수 있는 슬롯이 없는 모패밀리입니다' : undefined}
   onClick={() => onDetail(f.moFamilyHash)}
  >
   <FamilyAvatar name={f.name} familyHash={f.moFamilyHash} imageUrl={f.imageUrl} size={36} />
   <div className="flex-1 min-w-0">
    <div className="flex items-center gap-1.5 flex-wrap">
     <span className="font-semibold text-base truncate">{f.name}</span>
     {withRotateIcon && (
      <span className="text-base text-purple-700" title="바터 (체결됨)">🔄</span>
     )}
    </div>
    {/* 바터 쌍은 행마다 운영자가 다르다(내 바터 a 는 짝이, 짝의 with-b 는 내가 넣는다) — 행별로 명시. */}
    <OwnerOperatorLine family={f} className="mt-0.5" />
   </div>
  </div>
  );
 };

 return (
  <div className="bg-purple-50 border border-gray-200 border-l-4 border-l-purple-500">
   {renderRow(primary, true)}
   <div className="border-t border-gray-100" />
   {renderRow(secondary, false)}
   {(ownerName || pairName) && (
    <div className="flex items-center gap-2 px-3 py-2 border-t border-gray-100 bg-gray-50 flex-wrap">
     {ownerName && (
      <div className="flex items-center gap-1.5 min-w-0">
       <FamilyAvatar
        name={ownerName}
        familyHash={primary.ownerMoPeerHash || ownerName}
        imageUrl={primary.ownerProfileImage}
        size={20}
       />
       <span className="text-xs text-gray-500 truncate">{ownerName}</span>
      </div>
     )}
     {pairName && (
      <span className="text-xs text-purple-700 truncate" title={pairName}>↔ {pairName}</span>
     )}
    </div>
   )}
  </div>
 );
};
